import React, { useEffect, useState } from "react";
import { staffApi, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "./dateFmt";

/* Inline editor for the treatment (service) date on an invoice. Opens a
   Popover with a native date picker. Empty value resets the field to
   the backend-derived date (earliest linked appointment). Always
   editable — even after a full/partial payment — because operators
   frequently reschedule the physical treatment while keeping the
   invoice paid. */
export default function TreatmentDateEditor({ order, onChanged }) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(order.treatment_date || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => { setValue(order.treatment_date || ""); }, [order.treatment_date, order.id]);

  const save = async (nextVal) => {
    setBusy(true);
    try {
      await staffApi.patch(`/orders/${order.id}/treatment-date`, { treatment_date: nextVal || null });
      toast.success(nextVal ? "Treatment date updated" : "Treatment date reset");
      setOpen(false);
      if (onChanged) await onChanged();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex items-center gap-1">
      <span data-testid="order-treatment-date">
        {order.treatment_date
          ? fmtDate(order.treatment_date)
          : <span className="text-muted-foreground italic">Not set</span>}
        {order.treatment_date_source === "manual" && (
          <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-700">· manual</span>
        )}
      </span>
      <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button size="icon" variant="ghost" className="h-6 w-6" data-testid="edit-treatment-date-btn" title="Edit treatment date">
              <Pencil className="w-3.5 h-3.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-64 p-3 space-y-2" align="start">
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Treatment Date</div>
            <Input
              type="date"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="h-8"
              data-testid="treatment-date-input"
            />
            <div className="text-[10px] text-muted-foreground">
              Overrides the auto-derived date from linked appointments.
              Clear to revert to auto.
            </div>
            <div className="flex justify-between gap-2 pt-1">
              <Button size="sm" variant="ghost" onClick={() => save("")} disabled={busy} data-testid="treatment-date-reset">Reset</Button>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancel</Button>
                <Button size="sm" onClick={() => save(value)} disabled={busy || !value} data-testid="treatment-date-save">Save</Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
    </div>
  );
}
